import { useNavigate, useParams } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { usePatchProject, useProject } from "../../api/use-project"
import { useCallback, useContext, useEffect, useRef, useState } from "react"
import { Potree } from "./potree"
import { LayoutContext } from "../../contexts/layout-context"
import { Cameras } from "./cameras"
import { usePointCloud } from "./use-point-cloud"
import { useMap } from "./use-map"
import { useOrthomosaic } from "./use-orthomosaic"
import { LoadingPaper } from "../ui/loading-paper"
import { ErrorPaper } from "../ui/error-paper"
import { AuthContext } from "../../contexts/auth-context"
import { saveFile } from "../../helpers/file-saver"
import { createProjectFile, deleteProjectFile } from "../../api/project-api"
import { FullscreenDownloadProgress } from "../ui/fullscreen-download-progress.jsx"
import { useMutation } from '@tanstack/react-query'
import axios from "../../api/axios.js"

export const Editor = ({ isPublic }) => {
  const params = useParams()
  const navigate = useNavigate()
  const { t } = useTranslation(["editor", "project", "common"])
  const { isAuthenticated } = useContext(AuthContext)
  const [{ data: project, error, loading }] = useProject(
    params.projectId,
    isPublic
  )
  const [, executePatchProject] = usePatchProject(project?.id)
  const { setFullscreen } = useContext(LayoutContext)
  useEffect(() => {
    setFullscreen(true)
    return () => {
      setFullscreen(false)
    }
  }, [setFullscreen])
  const [potreeViewer, setPotreeViewer] = useState(null)
  const handlePotreeViewerChanged = useCallback((x) => {
    setPotreeViewer(x)
  }, [])
  const [guiLoaded, setGuiLoaded] = useState(false)
  const handlePotreeGuiLoaded = useCallback(() => {
    setGuiLoaded(true)
  }, [])

  const { projection } = usePointCloud(
    potreeViewer,
    project?.pointCloudUrl,
    project?.name,
    true
  )
  useMap(potreeViewer, projection, guiLoaded)
  useOrthomosaic(
    potreeViewer,
    project?.orthomosaicUrl,
    project?.pointCloudBounds,
    guiLoaded
  )

  useEffect(() => {
    if (!potreeViewer || !project.potreeProject) return
    window.Potree.loadProject(potreeViewer, project.potreeProject)
  }, [potreeViewer, project])

  const goBack = useCallback(() => {
    navigate("..", { relative: "path" })
  }, [navigate])

  useEffect(() => {
    if (!guiLoaded) return
    const e = document.getElementById("back_to_project_details")
    if (!e) return
    e.innerHTML = t("backToProjectDetails")
    e.addEventListener("click", goBack)
    return () => {
      e.removeEventListener("click", goBack)
    }
  }, [guiLoaded, goBack, t])

  const clipPointCloudAbortController = useRef()

  const clipPointCloudMutation = useMutation({
    mutationFn: async (data) => {
      clipPointCloudAbortController.current = new AbortController()
      return axios({
        method: "post",
        url: `/project/${params.projectId}/point-cloud/clip`,
        data: data,
        responseType: "blob",
        signal: clipPointCloudAbortController.current.signal
      })
    }
  })

  const downloadClippedPointCloud = useCallback(
    async (type) => {
      const scene = potreeViewer.scene
      const classifications = Object.keys(potreeViewer.classifications).filter(
        key =>
          !isNaN(Number(key)) &&
          potreeViewer.classifications[key].visible
      )
      const clipPolygons = scene.volumes
        .filter((x) => x.clip)
        .map((volume) => {
          volume.box.updateWorldMatrix(true, false)
          const verticies = volume.box.geometry.vertices.map((y) =>
            volume.box.localToWorld(y.clone())
          );
          //fix verticies order
          [
            verticies[2],
            verticies[3],
            verticies[4],
            verticies[5],
            verticies[6],
            verticies[7],
          ] = [
              verticies[3],
              verticies[2],
              verticies[7],
              verticies[6],
              verticies[4],
              verticies[5],
            ]
          verticies.push(verticies[0])
          return verticies
        })
      const center = potreeViewer.scene.pointclouds[0].boundingSphere.center
        .clone()
        .applyMatrix4(potreeViewer.scene.pointclouds[0].matrixWorld)
      try {
        const res = await clipPointCloudMutation.mutateAsync({ outputFormat: type, clipPolygons, center, classifications })
        saveFile(res.data, `${potreeViewer.scene.pointclouds[0].name}.${type}`)
      } catch (e) {
        console.log(e)
      }
    },
    [potreeViewer, clipPointCloudMutation.mutateAsync]
  )

  const onExportElementClick = useCallback((e) => {
    downloadClippedPointCloud(e.target.dataset.type)
    e.preventDefault()
  }, [downloadClippedPointCloud])

  useEffect(() => {
    if (!isAuthenticated || !potreeViewer) return
    potreeViewer.addEventListener("exports_changed", (e) => {
      for (const element of e.exportElements) {
        element.addEventListener("click", onExportElementClick)
      }
    })
  }, [potreeViewer, downloadClippedPointCloud, isAuthenticated, onExportElementClick])

  useEffect(() => {
    if (!guiLoaded) return
    const e = document.getElementById("sidebar_header")
    e.style.display = isAuthenticated ? "block" : "none"
  }, [guiLoaded, isAuthenticated])

  useEffect(() => {
    if (!isAuthenticated || !potreeViewer) return
    let lastProject = null
    const intervalId = setInterval(async () => {
      try {
        const project = window.Potree.saveProject(potreeViewer)
        if (lastProject !== null &&
          JSON.stringify(project) !== JSON.stringify(lastProject)
        ) {
          await executePatchProject({ data: { potreeProject: project } })
        }
        lastProject = project
      } catch (e) {
        console.log(e)
      }
    }, 1000)
    return () => {
      clearInterval(intervalId)
    }
  }, [potreeViewer, executePatchProject, isAuthenticated])

  useEffect(() => {
    if (!isAuthenticated || !potreeViewer || !project) return
    const userObjectAdded = async (e) => {
      const { object, file } = e
      const {
        data: { id, url },
      } = await createProjectFile(project.id, file)
      object.url = url
      object.projectFileId = id
      if (object.deleted) {
        // deleted during create
        await deleteProjectFile(project.id, id)
      }
    }
    potreeViewer.addEventListener("user_object_added", userObjectAdded)
    return () => {
      potreeViewer.removeEventListener("user_object_added", userObjectAdded)
    }
  }, [potreeViewer, project, isAuthenticated])

  useEffect(() => {
    if (!isAuthenticated || !potreeViewer || !project) return
    const userObjectDeleted = async (e) => {
      const { object } = e
      await deleteProjectFile(project.id, object.projectFileId)
    }
    potreeViewer.addEventListener("user_object_deleted", userObjectDeleted)
    return () => {
      potreeViewer.removeEventListener("user_object_deleted", userObjectDeleted)
    }
  }, [potreeViewer, project, isAuthenticated])

  if (loading) return <LoadingPaper />

  if (error)
    return (
      <ErrorPaper
        message={
          error.response?.status === 404
            ? t("notFoundError", { object: t("project") })
            : t("genericError", { action: t("loadProject") })
        }
      />
    )

  function handleCancelClipPointCloud() {
    clipPointCloudAbortController.current?.abort()
  }

  return (
    project && (
      <>
        <Potree
          onPotreeViewerChanged={handlePotreeViewerChanged}
          onGuiLoaded={handlePotreeGuiLoaded}
        />
        <Cameras potreeViewer={potreeViewer} data={project} />
        {clipPointCloudMutation.isPending && <FullscreenDownloadProgress
          totalTimeS={project.pointCloudCount * 0.00000041}
          onCancel={handleCancelClipPointCloud}
          title={"Exportálása folyamatban..."}
        />}
      </>
    )
  )
}
